/**
 * Keyboard shortcuts in layers, so only the topmost layer handles a key: an open modal, not the page beneath it.
 *
 * - The page is the base layer: `useShortcuts` outside any `<ShortcutLayer>` registers there.
 * - `<ShortcutLayer>` puts a layer on top for as long as it is mounted, and `useShortcuts` calls inside it register on
 *   that layer. A layer without shortcuts still blocks the layers beneath it, so every modal gets one: whoever renders a
 *   modal wraps it in a layer:
 *
 *       {showAlert && (
 *           <ShortcutLayer>
 *               <EnvironmentAlertModal ... />
 *           </ShortcutLayer>
 *       )}
 *
 * - A layer nested in another one lies on top of it; of two layers at the same depth the one mounted last is on top.
 * - A key goes to every enabled `useShortcuts` of the topmost layer that maps it, and its default action is prevented.
 *   Keys with Ctrl, Alt or Meta held are left alone, and so are printable keys (digits, letters) typed into an input:
 *   Enter, Escape and the function keys still work there.
 * - `<ShortcutLayer swallowPrintable>` also prevents the default of every printable key (a single character: digits,
 *   letters, space), for as long as it is the topmost layer. Preventing a keydown also suppresses its keypress event,
 *   which the barcode scanner listens to, so a scan is lost on such a layer instead of reaching the scanner. For modals
 *   whose digit shortcuts print or report something: a scan must not trigger them either. So a printable shortcut
 *   there acts once its key is released and no other key followed within `scanGap`, and not at all when its key came
 *   within `scanGap` of the one before: a scanner types its keys faster than that, a person does not. Keys typed into an
 *   input are still left to the input, Space to a focused button, and Tab, Enter, Escape and the other named keys are
 *   not printable.
 * - `usePreventFunctionKeys` prevents the default of the function keys apart from the layers, for a page that uses them.
 *
 * The barcode scanner (`use-barcode-scanner.ts`) listens on its own, to keypress: a scan works while a modal is open,
 * unless its layer swallows the printable keys.
 */
import type {ReactNode} from 'react';
import {createContext, use, useEffect, useEffectEvent, useLayoutEffect, useState} from 'react';

/** `event.key` (`'F1'`, `'1'`, `'Enter'`, `'Escape'`, ...) → handler */
export type Shortcuts = Partial<Record<string, (event: KeyboardEvent) => void>>;

type Layer = {
    depth: number;
    /** prevent the default of the printable keys no shortcut handles, see the top of this file */
    swallowPrintable: boolean;
};

type Registration = {
    layer: Layer;
    /** whether one of its shortcuts maps the key */
    maps(key: string): boolean;
    /** handles the event when one of its shortcuts maps the key, returns whether it did */
    handle(event: KeyboardEvent): boolean;
};

const baseLayer: Layer = {depth: 0, swallowPrintable: false};

/** (ms) keys closer together than this are a scan, as for the terminal's scanner (`avgTimeByChar` in `terminal.tsx`) */
const scanGap = 100;

/** the mounted layers above the base layer, with the order they were mounted in */
const layers = new Map<Layer, number>();
const registrations = new Set<Registration>();
let mounts = 0;

const LayerContext = createContext<Layer>(baseLayer);

const topLayer = (): Layer => {
    let top = baseLayer;
    let topOrder = 0;

    layers.forEach((order, layer) => {
        if (layer.depth > top.depth || (layer.depth === top.depth && order > topOrder)) {
            top = layer;
            topOrder = order;
        }
    });

    return top;
};

const isEditable = (element: EventTarget | null): boolean =>
    element instanceof HTMLElement &&
    ('INPUT' === element.tagName ||
        'TEXTAREA' === element.tagName ||
        'SELECT' === element.tagName ||
        element.isContentEditable);

/** Hand the event to the shortcuts of the layer, returns whether one of them mapped its key. */
const dispatch = (layer: Layer, event: KeyboardEvent): boolean => {
    let handled = false;

    // a copy: a handler may (un)register shortcuts, e.g. by closing a modal
    [...registrations].forEach(registration => {
        if (registration.layer === layer && registration.handle(event)) {
            handled = true;
        }
    });

    return handled;
};

/** A printable shortcut on a layer that swallows them, waiting for its key's release and then for `scanGap`. */
let pending: {event: KeyboardEvent; layer: Layer; timer?: number} | null = null;
let lastKeydown = -Infinity;

window.addEventListener('keydown', (event: KeyboardEvent): void => {
    // a held key repeats: it still acts once, on its release
    if (event.repeat && event.key === pending?.event.key) {
        event.preventDefault();

        return;
    }

    const sinceLastKey = event.timeStamp - lastKeydown;

    lastKeydown = event.timeStamp;

    // another key came: the pending one was (part of) a scan
    window.clearTimeout(pending?.timer);
    pending = null;

    // (browser autofill sends keydown events without a key)
    if (!event.key || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
        return;
    }

    // a single character is typed text in an input, not a shortcut; and Space presses a focused button
    if (
        1 === event.key.length &&
        (isEditable(event.target) || (' ' === event.key && event.target instanceof HTMLButtonElement))
    ) {
        return;
    }

    const layer = topLayer();

    if (layer.swallowPrintable && 1 === event.key.length) {
        event.preventDefault();

        const mapped = [...registrations].some(
            registration => registration.layer === layer && registration.maps(event.key),
        );

        if (mapped && !event.repeat && sinceLastKey >= scanGap) {
            pending = {event, layer};
        }

        return;
    }

    if (dispatch(layer, event)) {
        event.preventDefault();
    }
});

window.addEventListener('keyup', (event: KeyboardEvent): void => {
    if (!pending || pending.event.key !== event.key) {
        return;
    }

    const {layer} = pending;

    pending.timer = window.setTimeout(() => {
        const keydown = pending?.event;

        pending = null;

        // not when its modal closed (or another one opened) meanwhile
        if (keydown && topLayer() === layer) {
            dispatch(layer, keydown);
        }
    }, scanGap);
});

/**
 * Handle keyboard shortcuts while `enabled`, on the layer this component is rendered in (see the top of this file).
 * The handlers can change on every render: the latest ones are called.
 *
 *     useShortcuts({F9: () => props.onRequestClose(), '1': () => handleManco('Adres')}, !submitting);
 */
export const useShortcuts = (shortcuts: Shortcuts, enabled = true): void => {
    const layer = use(LayerContext);

    const maps = useEffectEvent(
        (key: string): boolean => Object.hasOwn(shortcuts, key) && undefined !== shortcuts[key],
    );

    const handle = useEffectEvent((event: KeyboardEvent): boolean => {
        const handler = Object.hasOwn(shortcuts, event.key) ? shortcuts[event.key] : undefined;

        handler?.(event);

        return undefined !== handler;
    });

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const registration: Registration = {layer, maps: key => maps(key), handle: event => handle(event)};

        registrations.add(registration);

        return () => {
            registrations.delete(registration);
        };
    }, [enabled, layer]);
};

const functionKey = /^F([1-9]|1[0-2])$/;

/**
 * Prevent the default action of F1 to F12 for as long as the component is mounted: F5 does not reload, F11 does not go
 * fullscreen, F3 does not open find, ... This works apart from the layers, so also for the keys no shortcut maps, with a
 * modal open and with Ctrl, Alt or Meta held: on a page where function keys are shortcuts, one pressed at the wrong
 * moment must not leave or change the page.
 */
export const usePreventFunctionKeys = (): void => {
    useEffect(() => {
        const preventDefault = (event: KeyboardEvent): void => {
            if (functionKey.test(event.key)) {
                event.preventDefault();
            }
        };

        window.addEventListener('keydown', preventDefault);

        return () => window.removeEventListener('keydown', preventDefault);
    }, []);
};

/** A layer of shortcuts on top of the one it is rendered in, for as long as it is mounted; for modals. */
export const ShortcutLayer = ({
    children,
    swallowPrintable = false,
}: {
    children: ReactNode;
    /** prevent the default of the printable keys no shortcut handles (and so lose a barcode scan), see the top of this file */
    swallowPrintable?: boolean;
}): React.JSX.Element => {
    const parent = use(LayerContext);
    const [layer] = useState<Layer>(() => ({depth: parent.depth + 1, swallowPrintable}));

    // a layout effect: the layer is in place before the modal's own shortcuts and before any key can be pressed
    // (StrictMode's remount in development only remounts a newly mounted layer: it stays the newest one)
    useLayoutEffect(() => {
        layers.set(layer, ++mounts);

        return () => {
            layers.delete(layer);
        };
    }, [layer]);

    return <LayerContext value={layer}>{children}</LayerContext>;
};
